import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, Inject, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, ValidatorFn } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { WorkspaceUser } from "@app/_classes/timeghost";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { useAbsencesForUser } from "@app/_helpers/absence/utils";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { pushError } from "@app/_helpers/globalErrorHandler";
import {
	ErrorResponse,
	isBreakRuleError,
	isWorkingHoursError,
	parseErrorResponse,
} from "@app/_helpers/is-error-object";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { hasPermissionByKey, hasUserRoleManager, isAdmin, isSubordinate, isSupervisor } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import { createRxValueWithCache } from "@app/_helpers/rx-value";
import { getComegoFutureLockedDate, getComegoLockedDate } from "@app/_helpers/timeLock";
import {
	coerceTimeFormat,
	createRxValue,
	DEFAULT_PERMISSION_GROUPS,
	distinctUntilChangedJson,
	fromRxValue,
	getActiveSchedule,
	hasPermission,
	nextTick,
	stringify,
} from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { ComegoSuggestionService } from "@app/services/come-and-go-suggestion.service";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { TimeDatePickerComponent } from "@app/shared/time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "@app/shared/time-date-picker/time-date-picker-config";
import { Order } from "@datorama/akita";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	ComegoTimeType,
	Logger,
	UserSettings,
	UserSettingsQuery,
	Workspace,
} from "@timeghost/web-api";
import {
	addDays,
	addMinutes,
	areIntervalsOverlapping,
	endOfDay,
	endOfISOWeek as endOfWeek,
	format,
	isBefore,
	isSameDay,
	isValid,
	roundToNearestMinutes,
	startOfDay,
	startOfISOWeek as startOfWeek,
} from "date-fns/esm";
import produce from "immer";
import { omit, pick, uniqBy, xorBy } from "lodash-es";
import {
	combineLatest,
	distinctUntilChanged,
	filter,
	firstValueFrom,
	map,
	of,
	startWith,
	switchMap,
	takeWhile,
	tap,
} from "rxjs";
import { firstBy } from "thenby";
import {
	ComeGoTimeControl,
	checkTimeOrder,
	generateTimeTableFromSchedule,
	maxLengthDescription,
} from "../come-and-go-create-dialog/come-and-go-create-dialog.component";
import {
	COMEGO_ICON_MAP,
	COMEGO_MAX_DESCRIPTION,
	resolveAbsenceCreatePermissions,
} from "../come-and-go-create-dialog/come-and-go-utils";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "../user-single-picker-dialog/user-single-picker-dialog.component";

type TimesSingleControl = ComeGoTimeControl & {
	recording: FormControl<boolean>;
	id: FormControl<string>;
	sourceRecordingId: FormControl<string | null>;
};
type TimesControl = FormGroup<TimesSingleControl>;
const log = new Logger("ComeAndGoMultiDialogComponent");
export const VerifyBlockComegoPast: (currentuser: () => UserSettings, choosenUser: () => string) => ValidatorFn = (
	_currentuser,
	_choosenUser
) => {
	return (form: FormControl<Date>) => {
		const blockComego = _currentuser().workspace.settings.blockComego;
		if (!blockComego) return null;
		const date = form.getRawValue();
		const currentuser = _currentuser();
		const choosenUser = _choosenUser();
		const isAdmin = !!currentuser.workspace.users.find((x) => x.admin && x.id === currentuser.id);
		const minDate = getComegoLockedDate(currentuser, choosenUser);
		const maxDate = getComegoFutureLockedDate(currentuser, choosenUser);
		if (minDate && maxDate) {
			if (date < minDate || date > maxDate) return { blockEdit: true };
			else return null;
		} else if (minDate) {
			if (date < minDate) return { blockEdit: true };
		} else if (maxDate) {
			if (date > maxDate) return { blockEdit: true };
		} else return null;
	};
};
export interface WorkingHoursUpdateData {
	allowedTimeTypes?: ComegoTimeType[];
	disabled?: boolean;
	disabledFields?: string[];
	enableNextDayNavigation?: boolean;
	error?: ErrorResponse;
	forceRecreateDay?: boolean;
	hiddenFields?: string[];
	time?: {
		value: ComegoTime[];
		correctTimeRanges?: boolean;
		lockItem?: boolean;
		refetchFromDate?: boolean;
		date: Date;
	};
	updateValidation?: boolean;
}
export type ComeGoTime = Omit<ComegoTime, "workspace" | "id"> & {
	recording?: boolean;
	typeId?: string;
	sourceRecordingId?: string | null;
};

export type absenceSubcategories = {
	id: string;
	enabled: boolean;
	title: string;
	description?: string;
	durationType?: "basedOnTargetHours" | "customValue";
	duration?: string | null;
	isStatic?: boolean;
	visibleUsers: any[];
	visibleForGroups: any[];
};
@UntilDestroy()
@ObserveFormGroupErrors()
@Component({
	selector: "tg-come-and-go-day-update-dialog",
	templateUrl: "./come-and-go-update-dialog.component.html",
	styleUrls: ["./come-and-go-update-dialog.component.scss"],
})
export class ComeAndGoMultiUpdateDialogComponent implements OnInit, AfterViewInit {
	private readonly clock = inject(ClockService);
	readonly iconMap = COMEGO_ICON_MAP;
	readonly isAMPM$ = this.userSettingsQuery.select().pipe(map((x) => !x.settings?.timeFormat24h));
	readonly now$ = this.clock.now$;
	readonly error$ = createRxValue<ErrorResponse>();
	absenceSubcategories: {
		id: string;
		enabled: boolean;
		title: string;
		description?: string;
		durationType?: "basedOnTargetHours" | "customValue";
		duration?: string | null;
		isStatic?: boolean;
		visibleUsers: any[];
		visibleForGroups: any[];
	}[] = [];
	constructor(
		private ref: MatDialogRef<ComeAndGoMultiUpdateDialogComponent>,
		private dialog: MatDialog,
		private userSettingsQuery: UserSettingsQuery,
		private app: AppService,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private recordService: RecordToolbarService,
		private comegoSuggestion: ComegoSuggestionService,
		private deletionService: DeletionUndoService,
		@Inject(MAT_DIALOG_DATA)
		private data: WorkingHoursUpdateData
	) {
		if (!this.data) this.data = {};
	}

	/**
	 * Ensures comego entries for the given date's week are loaded in the store (e.g. when switching to a day in another week).
	 * Fetches the week via API and upserts into the store so comegoQuery.getAll() for that day returns existing entries.
	 */
	private async ensureComegoLoadedForDate(date: Date, userId: string): Promise<void> {
		const weekStart = startOfWeek(date);
		const weekEnd = endOfWeek(date);
		const comego = await this.comegoService.getBetween(weekStart, weekEnd, userId).catch((err) => {
			pushError(err);
			return [];
		});
		if (comego?.length) {
			(this.comegoQuery.__store__ as ComegoStore).upsertMany(comego);
		}
	}

	readonly isLoading = createRxValue(false);
	readonly isDeletingEntries$ = createRxValue(false);
	readonly maxLength = COMEGO_MAX_DESCRIPTION;
	@ViewChild("confirmDeleteDialog", { static: true }) confirmDeleteDialog: TemplateRef<any>;
	private originalDate: Date | null = null;
	private activeSchedule: ReturnType<typeof getActiveSchedule> = null;
	get enableNextDayNavigation() {
		return !!this.data.enableNextDayNavigation;
	}
	readonly group = new FormGroup({
		user: new FormControl<Workspace["users"][0]>(null),
		times: new FormArray<TimesControl>([], {
			validators: [
				checkTimeOrder(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					},
					() => {
						const date = this.group?.getRawValue()?.date as Date;
						return date;
					},
					() => {
						const date = this.group?.getRawValue()?.date as Date;
						if (!date || !isValid(date)) return null;
						return {
							absence: 0,
							pause: 0,
							work: 0,
						} as { [K in ComegoTimeType]: number };
					},
					// last working day check
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const currentUser = this.group?.getRawValue()?.user as WorkspaceUser;
						const user = {
							...this.userSettingsQuery.getValue(),
							...((currentUser?.id && { id: currentUser?.id }) || {}),
						};
						const pastDay = addDays(date.getTime(), -1);
						const lastWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), pastDay) && x.user?.id === user.id,
							sortBy(a, b) {
								const aend = new Date(a.end || new Date()),
									bend = new Date(b.end || new Date());
								return bend.getTime() - aend.getTime();
							},
						})?.[0];
						if (!lastWorkingDayTime) return null;
						return {
							...lastWorkingDayTime,
							start: new Date(lastWorkingDayTime.start),
							end: new Date(lastWorkingDayTime.end || new Date()),
						};
					},
					// next working day check
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const user = this.userSettingsQuery.getValue();
						const dayOfTime = addDays(date.getTime(), 1);
						const nextWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), dayOfTime) && x.user?.id === user.id,
							sortBy(a, b) {
								const atime = new Date(a.start || new Date()),
									btime = new Date(b.start || new Date());
								return atime.getTime() - btime.getTime();
							},
							sortByOrder: Order.ASC,
						})?.[0];
						if (!nextWorkingDayTime) return null;
						return {
							...nextWorkingDayTime,
							start: new Date(nextWorkingDayTime.start),
							end: new Date(nextWorkingDayTime.end || new Date()),
						};
					},

					{
						ignoreRulesForRecordingTimes: true,
						resolveEntryTimes: (entry, selectedDate) => this.resolveFormEntryTimes(entry, selectedDate),
					}
				),
				maxLengthDescription(),
			],
			updateOn: "blur",
		}),
		date: new FormControl<Date>(new Date(), {
			validators: [
				VerifyBlockComegoPast(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					}
				),
			],
		}),
	});

	/** Set when we delete from this dialog so we can refresh existing entries on undo. */
	private lastDeletedViewId: string | null = null;
	/** IDs of recording entries that were checked out locally; updated on submit. */
	private checkedOutRecordingIds: string[] = [];
	/** Full end datetimes for checked-out recordings, keyed by original recording id. */
	private checkedOutRecordingEnds = new Map<string, Date>();
	/** Open recording to create after work/pause toggle via handleUpdate(). */
	private pendingToggleRecording: {
		type: ComegoTimeType;
		start: Date;
		user: WorkspaceUser;
	} | null = null;
	readonly existingEntries$ = createRxValue<ComegoTime[]>([]);
	readonly duration$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
		this.now$,
	]).pipe(
		map(([_, existingEntries, now]) => {
			const x = this.group.getRawValue();
			const user = this.userSettingsQuery.getValue();
			const wh = user.workspace.schedules.workspace["workinghours"] as any;
			const shortBreakDuration = (wh?.minimumBreakDurationForCredit ?? 0.25) * 3600;

			// Start with existing entries duration
			const baseSum = (existingEntries || []).reduce(
				(acc, r) => {
					if (!r.start || !r.end) return acc;
					const start = new Date(r.start).getTime(),
						end = new Date(r.end).getTime();
					if (end < start) return acc;
					const sum = (end - start) / 1000;
					if (r.type === "work") acc.working += sum;
					else if (r.type === "pause") {
						acc.pause += sum;
						if (sum > shortBreakDuration) acc.realBreak += sum;
					} else if (r.type === "absence") acc.absence += sum;
					return acc;
				},
				{ working: 0, pause: 0, realBreak: 0, absence: 0 }
			);

			// Add form entries duration
			return x.times.reduce((acc, r) => {
				if (!r.start || !r.end) return acc;
				const resolved = this.resolveFormEntryTimes(r, x.date);
				const start = resolved?.start.getTime() ?? coerceTimeFormat(r.start!).getTime();
				const end = r.recording
					? now.getTime()
					: (resolved?.end.getTime() ?? coerceTimeFormat(r.end!).getTime());
				if (end < start) return acc;
				const sum = (end - start) / 1000;
				if (r.type === "work") acc.working += sum;
				else if (r.type === "pause") {
					acc.pause += sum;
					if (sum > shortBreakDuration) acc.realBreak += sum;
				} else if (r.type === "absence") acc.absence += sum;
				else acc.absence += sum;
				return acc;
			}, baseSum);
		})
	);
	readonly overlappingExistingEntryIds$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
	]).pipe(
		map(([_, existingEntries]) => {
			const x = this.group.getRawValue();
			const times = x.times;
			const date = x.date;
			const overlappingIds = new Set<string>();

			if (!existingEntries?.length || !date) return [];

			for (const formEntry of times) {
				if (formEntry.type !== "work" && formEntry.type !== "pause") continue;
				if (!formEntry.start || !formEntry.end || formEntry.recording) continue;

				const resolved = this.resolveFormEntryTimes(formEntry, date);
				if (!resolved || resolved.start >= resolved.end) continue;
				const formStart = resolved.start;
				const formEnd = resolved.end;

				for (const existing of existingEntries) {
					if (existing.type !== "work" && existing.type !== "pause") continue;
					if (!existing.start || !existing.end || !existing.id) continue;

					const existingStart = new Date(existing.start);
					const existingEnd = new Date(existing.end);
					if (!isValid(existingStart) || !isValid(existingEnd) || existingStart >= existingEnd) continue;

					if (
						areIntervalsOverlapping(
							{ start: formStart, end: formEnd },
							{ start: existingStart, end: existingEnd }
						) &&
						!(
							formEnd.getTime() === existingStart.getTime() ||
							existingEnd.getTime() === formStart.getTime()
						)
					) {
						overlappingIds.add(existing.id);
					}
				}
			}

			return Array.from(overlappingIds);
		})
	);
	readonly workspace$isAdmin = fromRxValue(
		this.userSettingsQuery.select((x) => hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, x))
	);
	readonly workspace$canManageOthers = fromRxValue(
		this.userSettingsQuery.select((x) => isAdmin(x) || isSupervisor(x) || hasUserRoleManager(x))
	);
	readonly canUpdateTimes$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes" as any)));
	readonly canDelete$ = combineLatest([
		this.canUpdateTimes$,
		this.group.valueChanges.pipe(startWith(this.group.value)),
	]).pipe(map(([canUpdate]) => canUpdate || this.hasActiveRecording));
	readonly preventDeviatingWorkingHours = this.userSettingsQuery
		.select()
		.pipe(
			map((userSettings) => userSettings.workspace.schedules.workspace.workinghours.preventDeviatingWorkingHours)
		);
	private readonly rulesEnforced$ = this.preventDeviatingWorkingHours.pipe(
		startWith(
			!!this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours
				?.preventDeviatingWorkingHours
		)
	);
	readonly activeRecording$ = createRxValue<FormGroup<any> | undefined>(undefined);
	/** True when existing + form entries combined would meet or exceed the daily max work duration (disables add button). Subscribes to form/entries/wh only when rules are enforced. */
	readonly isDailyMaxReachedOrExceededByCombined$ = this.rulesEnforced$.pipe(
		switchMap((enforced) => {
			if (!enforced) return of(false);
			return combineLatest([
				this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
				this.existingEntries$.value$,
				this.userSettingsQuery.select(
					(u) =>
						(u.workspace?.schedules as { workspace?: { workinghours?: { dailyMaxHours?: number } } })
							?.workspace?.workinghours
				),
			]).pipe(
				map(([_, existingEntries, wh]) => {
					const raw = this.group.getRawValue();
					const date = raw?.date as Date | null;
					if (!date || !isValid(date)) return false;
					const formEntries = (raw?.times ?? []).map(
						(t: { start?: string; end?: string; type: ComegoTimeType }) => ({
							start: t.start ?? "",
							end: t.end ?? "",
							type: t.type,
						})
					);
					return this.comegoSuggestion.isDailyMaxReachedOrExceededByCombined(
						date,
						existingEntries ?? [],
						formEntries,
						wh ?? undefined
					);
				})
			);
		})
	);
	getErrorObservable(controlName: string) {
		const usersettings = this.userSettingsQuery.getValue();
		const aliasMap = {
			name: "time.name",
			start: "timer.time.start",
			end: "timer.time.end",
		};
		const aliasName = aliasMap[controlName] || controlName;
		return useFormErrorObservable(this, { respectState: true })(
			controlName,
			() => this.group.controls[controlName],
			{
				required: (error, ctrl) => {
					if (controlName === "task") return { content: "errors.record.desc-required", args: {} };
					if (controlName === "project") return { content: "errors.record.project-req", args: {} };
					if (controlName === "times") return { content: "errors.times.comego.required", args: {} };
					return {
						content: "errors.required",
						args: { field: aliasName },
					};
				},
				same: (error, ctrl) => {
					const aliasFields = Object.entries(ctrl.errors?.fields || {}).reduce(
						(acc, [key, value]) => ({ ...acc, [key]: aliasMap[value as string] }),
						{}
					);
					return { content: "errors.sameValue", args: { ...aliasFields } };
				},
				breakRule: (error) => {
					if (error.type === "break")
						return {
							content: "errors.times.comego.breakRuleNotice",
							args: pick(error, "workingHours", "minutesDue"),
						};
					if (error.type === "rest_prev")
						return {
							content: "errors.times.comego.breakBetweenWorkingDays_prev",
							args: {
								breakBetweenWorkingDays: error.value,
								previousDayEnd: error.args.previousDayEnd,
								thisDayStart: error.args.thisDayStart,
							},
						};
					if (error.type === "rest_next")
						return {
							content: "errors.times.comego.breakBetweenWorkingDays_next",
							args: {
								breakBetweenWorkingDays: error.value,
								nextDayStart: error.args.nextDayStart,
								thisDayEnd: error.args.thisDayEnd,
							},
						};
					if (error.type === "maxHours")
						return { content: "errors.times.comego.dailyMaxHours", args: { dailyMaxHours: error.value } };
					return null;
				},
				minlength: (error, ctrl) => ({
					content: "errors.minlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlength: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlengthDescription: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: "Description", length: COMEGO_MAX_DESCRIPTION },
				}),
				sameType: (error, ctrl) => {
					return { content: "Subsequent entries must not be the same type", args: {} };
				},
				missingType: (error, ctrl) => {
					return { content: "Entries are missing a stop entry", args: {} };
				},
				invalidType: (error, ctrl) => {
					return { content: 'Before last type must not be of type "pause"', args: {} };
				},
				intervening: (error, ctrl) => {
					return { content: "errors.times.comego.intervening", args: {} };
				},
				breakToShort: (error, ctrl) => {
					const settings = usersettings.workspace.schedules.workspace["workinghours"] as any;
					return {
						content: "errors.times.comego.shortBreak",
						args: { length: (settings?.minimumBreakDurationForCredit ?? 0.25) * 60 },
						warning: true,
					};
				},
				blockEdit: (error, ctrl) => {
					return { content: "errors.times.comego.blockEdit", args: {} };
				},
				blockEditFuture: (error, ctrl) => {
					return { content: "errors.times.comego.blockEditFuture", args: {} };
				},
				blockComegoPast: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoPast",
						args: { value: blockComego.period, range: blockComego.range },
					};
				},
				blockComegoFuture: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoFuture",
						args: { value: blockComego.futurePeriod, range: blockComego.futureRange },
					};
				},
			},
			(key) => key,
			{
				initialValidate: false,
			}
		);
	}
	readonly postDiffUser$ = combineLatest([
		this.group.valueChanges.pipe(
			map((x) => x?.user?.id),
			distinctUntilChanged()
		),
		this.userSettingsQuery.select(),
	]).pipe(map(([uid, user]) => uid && uid !== user.id));

	readonly canCreateAbsencesForSelectedUser$ = combineLatest([
		this.group.valueChanges.pipe(
			map((x) => x?.user?.id),
			distinctUntilChanged(),
			startWith(this.group.getRawValue()?.user?.id)
		),
		this.userSettingsQuery.select(),
	]).pipe(map(([selectedUserId, user]) => resolveAbsenceCreatePermissions(user, selectedUserId || user.id)));

	readonly currentDateHasSchedule$ = combineLatest([this.group.valueChanges, this.userSettingsQuery.select()]).pipe(
		map(([value, user]) => {
			const { date } = this.group.getRawValue() ?? {};
			if (!date) return null;
			const sched = getActiveSchedule({ ...user, id: value.user?.id || user.id }, date);
			return sched?.enabled && !!sched.items.find((x) => x.day === startOfDay(date).getUTCDay() && x.enabled);
		})
	);
	readonly hiddenFields = createRxValue<{ [key: string]: boolean }>({});
	readonly canAddTime = createRxValue(true);
	readonly enabledTypes = createRxValue<{ [K in ComegoTimeType]?: boolean }>({
		absence: true,
		pause: true,
		work: true,
	});
	readonly comegoDescriptionToggle$ = createRxValueWithCache<boolean>("comgoDescriptionToggle", false);
	readonly userHasChangedDescription = createRxValue<boolean>(false);
	private newWorkEntryDuration: number | null = null;
	async ngOnInit() {
		this.ref.updateSize("560px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);
		const now = new Date();
		const userSettings = this.userSettingsQuery.getValue();
		const userFromTime = this.data.time?.value?.find((d) => d.user)?.user ?? { id: userSettings.id };
		const user = userSettings.workspace.users?.find((d) => d.id === userFromTime.id) as WorkspaceUser;
		if (this.data.time.refetchFromDate && user) {
			const start = startOfDay(this.data.time.date),
				end = endOfDay(start);
			this.isLoading.next(true);
			this.data.time.value = await this.comegoService.adapter
				.post<any>("/get/comego", {
					$filter: `start le '${end.toISOString()}' and start ge '${start.toISOString()}' and user__id eq '${user.id}'`,
				})
				.toPromise()
				.finally(() => {
					this.isLoading.next(false);
				});
		}
		this.group.patchValue({
			user,
			date: this.data.time?.date ?? now,
		});
		this.originalDate = new Date(this.data.time?.date ?? now);
		this.activeSchedule = getActiveSchedule({ ...userSettings, id: user.id }, this.group.getRawValue().date);

		if (this.data?.hiddenFields?.length)
			this.hiddenFields.next(this.data.hiddenFields.reduce((acc, r) => ({ ...acc, [r]: true }), {}));
		this.group.updateValueAndValidity();
		let error: any = this.data.error;
		if (isBreakRuleError(error)) pushError(error.message, error.args);
		error = null;

		this.newWorkEntryDuration = 30 * 60 * 1000; // default 30 min until API returns
		const userId = this.group.getRawValue()?.user?.id ?? this.userSettingsQuery.getValue().id;
		this.comegoSuggestion.getLastWorkEntryDurationMs(userId).then((duration) => {
			if (duration != null && duration > 0) this.newWorkEntryDuration = duration;
		});

		// auto generate time ranges based on time values
		nextTick(() => {
			this.data.time.value?.forEach((t) => {
				const start = new Date(t.start);
				if (t["typeId"]) {
					t.type = t["typeId"];
				}
				this.addTimeRange(
					t.type,
					start,
					new Date(t.end ?? (!isSameDay(start, now) ? endOfDay(start) : now)),
					false,
					!t.end,
					t.name,
					t.id
				);
			});
			this.group.controls.times.updateValueAndValidity();
			this.sortTimes(true);
		});

		// validate and check errors on next tick, updates ui instantly
		if (error) {
			nextTick(() => {
				this.error$.next(error);
				if (!this.data.disabled && isWorkingHoursError(error))
					this.checkValueOnceChange(this.group.getRawValue().times);
				if (this.data.disabled) {
					this.group.markAsPristine();
					this.group.disable();
				}
			});
		}
		if (this.data.enableNextDayNavigation) {
			this.group.controls.date.disable();
		}
		if (!this.data.disabled && this.data.disabledFields?.length)
			nextTick(() => {
				this.data.disabledFields.forEach((x) => {
					if (this.group.controls[x]?.enabled) {
						this.group.controls[x].disable();
					} else {
						if (x === "time.type")
							this.group.controls.times.controls.forEach((t) => t.controls.type.disable());
						if (x === "time") {
							this.group.controls.times.controls.forEach((t) => t.disable());
							this.group.controls.times.disable();
						}
						if (x === "time.add") this.canAddTime.next(false);
					}
				});
			});

		if (this.data.allowedTimeTypes?.length) {
			this.enabledTypes.next(this.data.allowedTimeTypes.reduce((acc, r) => ({ ...acc, [r]: true }), {}));
		}
		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => x?.date),
				distinctUntilChangedJson()
			)
			.subscribe(async () => {
				const value = this.group.getRawValue();
				const u = value?.user;
				const date = value?.date;
				if (u && date)
					this.activeSchedule = getActiveSchedule({ ...this.userSettingsQuery.getValue(), id: u.id }, date);
				await this.loadExistingEntries();
				this.group.controls.times.updateValueAndValidity();
			});

		// Listen for end time changes to convert "00:00" to "23:59"
		this.group.controls.times.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
			this.group.controls.times.controls.forEach((timeControl, index) => {
				const endControl = timeControl.get("end");
				if (endControl && endControl.value === "00:00") {
					endControl.patchValue("23:59", { emitEvent: false });
				}
			});
		});

		// this.group.valueChanges.pipe(take(1)).subscribe(() => {
		// 	this.sortTimes(true);
		// 	this.group.setMustChange(this.group.getRawValue());
		// });
		this.userSettingsQuery
			.select()
			.pipe(untilDestroyed(this))
			.subscribe((user) => {
				this.canAddTime.next(hasPermissionByKey(user, "groupsCanComegoCreateTimes"));
			});
		this.absenceSubcategories = userSettings?.workspace?.["workinghours"]?.["absenceSubcategories"] || [];
		this.deletionService.restored
			.pipe(
				filter((ev) => ev.type === "comegoDay" && ev.key === this.lastDeletedViewId),
				untilDestroyed(this)
			)
			.subscribe(() => {
				this.loadExistingEntries();
				this.lastDeletedViewId = null;
			});
		if (this.data.updateValidation) {
			this.group.markAllAsTouched();
			this.group.markAsDirty();
			this.group.updateValueAndValidity();
		}
	}
	ngAfterViewInit(): void {
		this.group.updateValueAndValidity();
	}
	// private sortingState = false;
	updatePreviousEntryEndTime(ev: Event, currentIndex: number) {
		this.runTimesBlurAction(ev, currentIndex, (index) => {
			const timesControls = this.group.controls.times.controls;
			const previousIndex = index - 1;

			if (previousIndex >= 0) {
				const currentControl = timesControls[index];
				const previousControl = timesControls[previousIndex];
				if (
					currentControl &&
					previousControl &&
					(previousControl.controls.type.value === "work" || previousControl.controls.type.value === "pause")
				) {
					const currentStartTime = currentControl.controls.start.value;
					if (currentStartTime) {
						previousControl.controls.end.patchValue(currentStartTime, { emitEvent: false });
						previousControl.updateValueAndValidity();
						currentControl.updateValueAndValidity();
					}
				}
			}
		});
	}

	updateNextEntryStartTime(ev: Event, currentIndex: number) {
		this.runTimesBlurAction(ev, currentIndex, (index) => {
			const timesControls = this.group.controls.times.controls;
			const nextIndex = index + 1;

			if (nextIndex < timesControls.length) {
				const currentControl = timesControls[index];
				const nextControl = timesControls[nextIndex];
				if (
					currentControl &&
					nextControl &&
					(nextControl.controls.type.value === "work" || nextControl.controls.type.value === "pause")
				) {
					const currentEndTime = currentControl.controls.end.value;
					if (currentEndTime) {
						nextControl.controls.start.patchValue(currentEndTime, { emitEvent: false });
						nextControl.updateValueAndValidity();
						currentControl.updateValueAndValidity();
					}
				}
			}
		});
	}

	sortTimesOnBlur(ev: Event) {
		this.runTimesBlurAction(ev);
	}

	private runTimesBlurAction(ev: Event, currentIndex?: number, action?: (index: number) => void) {
		if (this.group.pristine || this.group.controls.times.length < 2) return;
		this.group.markAllAsTouched();
		const currentTarget = ev.currentTarget as HTMLElement;
		requestAnimationFrame(() => {
			if (currentTarget.contains(document.activeElement)) return;

			if (action && currentIndex !== undefined && currentIndex >= 0) {
				action(currentIndex);
			}

			this.sortTimes();
			// if (!this.sortingState) {
			// this.sortingState = true;
			// currentTarget.focus();
			// log.debug({ sortingEventSource: currentTarget });
			// setTimeout(() => {
			//   this.sortingState = false;
			// }, 100);
			// }
		});
	}
	private sortTimes(force: boolean = false) {
		if (!force && (this.group.pristine || this.group.controls.times.length < 2)) return;
		const { times } = this.group.getRawValue();
		const sortBy = firstBy((d: any) => coerceTimeFormat(d.start).getTime(), "asc").thenBy(
			(d: any) => coerceTimeFormat(d.end ?? d.start).getTime(),
			"asc"
		);
		// this.group.patchValue({
		//   times: times.sort(sortBy),
		// });
		// also sort control instances - so static options are maintained to their origin value
		this.group.controls.times.controls = this.group.controls.times.controls.toSorted((a, b) =>
			sortBy(a.getRawValue(), b.getRawValue())
		);
		// todo - sorted clone to controls to kind of fix focus issue on re-sort
		// this.group.controls.times.controls = this.group.controls.times.controls.toSorted(
		//   firstBy((d: any) => coerceTimeFormat(d.value.start).getTime(), 'asc').thenBy(
		//     (d) => coerceTimeFormat(d.value.end ?? d.value.start).getTime(),
		//     'asc',
		//   ),
		// );
		log.debug({ sortedTimes: times });
		this.group.updateValueAndValidity();
		this.group.controls.times.updateValueAndValidity();
	}
	private markFormAsChanged() {
		this.group.controls.times.markAsDirty();
		this.group.markAsDirty();
	}
	resetToSchedule() {
		const { date, user: selectedUser } = this.group.value;
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: (selectedUser || user).id }, date);
		this.group.controls.times.clear({ emitEvent: true });
		this.group.patchValue({
			times: [],
		});
		if (times)
			times.forEach((x: ComegoTime) => {
				const isRecording = !x.end;
				this.group.controls.times.push(
					new FormGroup({
						name: new FormControl<string>(""),
						id: new FormControl(x.id ?? null),
						sourceRecordingId: new FormControl<string | null>(null),
						type: new FormControl({ value: x.type || "work", disabled: isRecording }),
						start: new FormControl(x.start!),
						end: new FormControl(x.end!),
						recording: new FormControl(isRecording),
						user: new FormControl(null),
					})
				);
			});
		this.group.updateValueAndValidity();
		this.markFormAsChanged();
	}
	openUserPicker() {
		if (this.hasActiveRecording) {
			return;
		}

		const user = this.group.value?.user;
		if (!user) return;
		this.dialog
			.open(UserSinglePickerDialogComponent, {
				data: <UserSinglePickerDialogData>{
					selected: this.group.value?.user,
					filter: ({ selected, ...x }) => {
						const status = ((user) => parseSubscriptionAsStatus(user.workspace, user))(
							this.userSettingsQuery.getValue()
						);
						return ((x.has_license || status.isTrial) && !x.removed) || selected;
					},
				},
			})
			.afterClosed()
			.subscribe((user) => {
				if (user) {
					this.patchValue({
						user,
					});
					this.group.controls.date.updateValueAndValidity();
				}
			});
		this.markFormAsChanged();
	}
	calculateDuration(ctrl: TimesControl, ctrlIndex: number) {
		const value = ctrl.getRawValue();
		if (!value) return null;
		const start = coerceTimeFormat(value.start!).getTime(),
			end = coerceTimeFormat(value.end!).getTime();
		if (end < start) return null;
		return (end - start) / 1000;
	}
	get groupValue() {
		return this.group.getRawValue();
	}
	addTimeRange(
		type: ComegoTimeType,
		start: Date,
		end: Date,
		emitChangesOnAdd: boolean = true,
		recording: boolean = false,
		name: string,
		timeId?: string
	) {
		const isDisabledControl = (name: string) => !!this.data.disabledFields?.find((x) => x === name);
		const isExistingEntry = !!timeId;
		const canUpdateTimes = hasPermissionByKey(
			this.userSettingsQuery.getValue(),
			"groupsCanComegoUpdateTimes" as any
		);
		const lockRecordingFields = recording && !canUpdateTimes;
		const newTimeGroup = new FormGroup<TimesSingleControl>({
			name: new FormControl<string>(name),
			id: new FormControl(timeId ?? null),
			sourceRecordingId: new FormControl<string | null>(null),
			start: new FormControl({ value: format(start, "HH:mm"), disabled: lockRecordingFields }),
			end: new FormControl(format(end, "HH:mm")),
			type: new FormControl({
				value: type,
				disabled: isDisabledControl("time.type") || recording,
			}),
			recording: new FormControl(recording),
			user: new FormControl(null),
		});
		this.group.controls.times.push(newTimeGroup, { emitEvent: false });
		if (recording) this.activeRecording$.update(newTimeGroup);

		// Only parse duration for new entries (not existing ones loaded from database)
		// For existing entries, preserve the saved end time from the database
		if (!isExistingEntry) {
			this.parseTimeDurationFromType(newTimeGroup);
		}

		// Set up change listeners for the newly added time group
		// These will trigger when user manually changes type or start time
		newTimeGroup.controls.start.valueChanges.subscribe(() => {
			this.parseTimeDurationFromType(newTimeGroup);
		});
		newTimeGroup.controls.type.valueChanges.subscribe(() => {
			this.parseTimeDurationFromType(newTimeGroup);
		});

		if (emitChangesOnAdd) this.group.controls.times.updateValueAndValidity();
	}
	addTime(type?: ComegoTimeType, duration?: number, reversi?: boolean) {
		const date = this.group.getRawValue()?.date || new Date();
		const timeValues = this.group.controls.times.getRawValue();
		const isDisabledControl = (name: string) => !!this.data.disabledFields?.find((x) => x === name);
		// Chronologically last form entry (by end time) for consistent suggestion
		const formEntriesWithEnd =
			timeValues?.map((t) => ({ endTs: coerceTimeFormat(t.end, date).getTime(), type: t.type })) ?? [];
		const lastFormByTime =
			formEntriesWithEnd.length > 0 ? formEntriesWithEnd.reduce((a, b) => (a.endTs >= b.endTs ? a : b)) : null;
		const wh = this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.["workinghours"];

		const suggestion = this.comegoSuggestion.getSuggestedNextEntry({
			date,
			existingEntries: this.existingEntries$.value ?? [],
			formEntries: timeValues?.map((t) => ({ start: t.start, end: t.end, type: t.type })) ?? [],
			lastFormEntry: lastFormByTime ? { endTs: lastFormByTime.endTs, type: lastFormByTime.type } : undefined,
			schedule: this.activeSchedule,
			workingHours: wh ?? undefined,
			newWorkEntryDurationMs: this.newWorkEntryDuration ?? undefined,
			preferredType: type ?? undefined,
		});
		const nextType = suggestion.type;
		const prevTime = suggestion.startTime;
		const nextTime = suggestion.endTime;

		if (this.data.allowedTimeTypes && !this.data.allowedTimeTypes?.find((x) => x === nextType)) return;
		if (reversi)
			this.group.controls.times.insert(
				0,
				new FormGroup<TimesSingleControl>({
					name: new FormControl<string>(null),
					id: new FormControl(null),
					sourceRecordingId: new FormControl<string | null>(null),
					end: new FormControl(format(nextTime, "HH:mm")),
					type: new FormControl({ value: nextType, disabled: isDisabledControl("time.type") }),
					start: new FormControl(format(prevTime, "HH:mm")),
					recording: new FormControl(false),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		else
			this.group.controls.times.push(
				new FormGroup<TimesSingleControl>({
					name: new FormControl<string>(null),
					id: new FormControl(null),
					sourceRecordingId: new FormControl<string | null>(null),
					end: new FormControl(format(nextTime, "HH:mm")),
					type: new FormControl({ value: nextType, disabled: isDisabledControl("time.type") }),
					start: new FormControl(format(prevTime, "HH:mm")),
					recording: new FormControl(false),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		this.group.controls.times.updateValueAndValidity();
		this.markFormAsChanged();
	}
	async deleteItem(index: number, confirmDiscardTemplate?: TemplateRef<any>) {
		const ctrl = this.group.controls.times.at(index);
		const isRecording = ctrl.controls.recording.value === true;

		if (isRecording && confirmDiscardTemplate) {
			const allowDelete = await firstValueFrom(
				this.dialog
					.open(confirmDiscardTemplate, {
						data: { entity: this.data },
					})
					.afterClosed()
			);
			if (allowDelete !== true) return;
		}

		const wasActiveRecording = ctrl === this.activeRecording$.value;
		this.group.controls.times.removeAt(index);
		if (wasActiveRecording) {
			this.activeRecording$.update(undefined);
			const nextActiveRecording = this.group.controls.times.controls.find(
				(x) => x.controls.recording.value === true
			);
			if (nextActiveRecording) {
				this.activeRecording$.update(nextActiveRecording);
			}
		}
		this.group.controls.times.controls.forEach((x) => x.updateValueAndValidity());
		this.group.updateValueAndValidity();
		this.group.markAllAsTouched();
		this.markFormAsChanged();
	}
	private canAutoSubmit(): boolean {
		if (
			!this.userSettingsQuery.getValue().workspace.schedules?.workspace?.["workinghours"]
				?.preventDeviatingWorkingHours
		)
			return true;
		const timesErrors = this.group.controls.times.errors;
		const isShortBreakWarningOnly =
			!!timesErrors?.breakToShort && Object.keys(timesErrors).every((k) => k === "breakToShort" || k === "index");

		const timesBlocksSubmit = timesErrors && !isShortBreakWarningOnly;
		const dateBlocksSubmit = !!this.group.controls.date.errors;

		if (timesBlocksSubmit || dateBlocksSubmit) return false;
		if (this.error$.value) return false;
		return true;
	}
	private resolveFormEntryTimes(
		entry: { start?: string; end?: string; sourceRecordingId?: string | null },
		formDate: Date
	): { start: Date; end: Date } | null {
		const sourceId = entry.sourceRecordingId;
		if (sourceId && this.checkedOutRecordingEnds.has(sourceId)) {
			const original = this.data.time.value?.find((o) => o.id === sourceId);
			const end = this.checkedOutRecordingEnds.get(sourceId)!;
			if (!original?.start) return null;
			return { start: new Date(original.start), end };
		}
		const start = coerceTimeFormat(entry.start!, formDate);
		const end = coerceTimeFormat(entry.end!, formDate);
		if (!isValid(start) || !isValid(end)) return null;
		return { start, end };
	}
	private getActiveOpenRecording(): ComegoTime | null {
		const ctrl = this.group.controls.times.controls.find((x) => x.controls.recording.value === true);
		if (!ctrl) return null;
		const { id } = ctrl.getRawValue();
		const original = id ? this.data.time.value?.find((o) => o.id === id) : null;
		const type =
			typeof original?.type === "string"
				? original.type
				: (original?.type as { name?: string } | undefined)?.name;
		if (!original?.start || type !== "work") return null;
		const user = original.user ?? this.group.getRawValue().user;
		return { ...original, end: null, type: "work", user } as ComegoTime;
	}
	private async maybeResolveBeforeActiveRecordingStop(): Promise<boolean> {
		const openRecording = this.getActiveOpenRecording();
		if (!openRecording) return true;
		if (await this.recordService.hasStopRuleViolation(openRecording)) {
			this.ref.close(null);
			await this.recordService.resolveComegoStopAfterClosingParent(openRecording);
			return false;
		}
		return true;
	}
	private stopActiveRecordingInForm(): Date | null {
		const ctrl = this.group.controls.times.controls.find((x) => x.controls.recording.value === true);
		if (!ctrl) return null;
		const { id, recording } = ctrl.getRawValue();
		if (!recording) return null;

		const original = id ? this.data.time.value?.find((o) => o.id === id) : null;
		let end = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		if (original?.start && isBefore(end, new Date(original.start))) end = new Date(original.start);

		if (id) {
			this.checkedOutRecordingIds.push(id);
			this.checkedOutRecordingEnds.set(id, end);
			ctrl.controls.sourceRecordingId.patchValue(id, { emitEvent: false });
		}

		ctrl.controls.start.enable({ emitEvent: false });
		ctrl.controls.type.enable({ emitEvent: false });
		ctrl.controls.end.patchValue(format(end, "HH:mm"));
		ctrl.controls.recording.patchValue(false);
		ctrl.controls.id.patchValue(null);

		ctrl.updateValueAndValidity();
		this.group.controls.times.updateValueAndValidity();
		this.activeRecording$.update(undefined);
		this.markFormAsChanged();
		return end;
	}
	checkOutActiveRecording() {
		void this.checkOutActiveRecordingAsync();
	}
	private async checkOutActiveRecordingAsync() {
		if (!(await this.maybeResolveBeforeActiveRecordingStop())) return;
		const stoppedAt = this.stopActiveRecordingInForm();
		if (!stoppedAt) return;
		this.group.updateValueAndValidity();
		if (this.canAutoSubmit()) {
			await this.submit();
		}
	}
	private async handleUpdate() {
		const previousRecording = this.activeRecording$.value;
		if (!previousRecording) return;
		if (!(await this.maybeResolveBeforeActiveRecordingStop())) return;
		const stoppedAt = this.stopActiveRecordingInForm();
		if (!stoppedAt) return;
		const user = this.group.getRawValue().user;
		if (!user) return;
		const previousType = previousRecording.getRawValue().type;
		const type = previousType === "work" ? "pause" : "work";
		this.pendingToggleRecording = { type, start: stoppedAt, user };
		this.markFormAsChanged();

		if (this.canAutoSubmit()) {
			await this.submit();
		} else {
			this.addTimeRange(type, stoppedAt, addMinutes(stoppedAt, 1), true, true, "");
		}
	}
	patchValue(data: Partial<typeof this.group.value> = {}, options?: Parameters<typeof this.group.patchValue>[1]) {
		const user = this.userSettingsQuery.getValue();
		if (data.user === null) data.user = user.workspace.users.find((x) => x.id === user.id);
		return this.group.patchValue(data, options);
	}

	private checkValueOnceChange(value: any) {
		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => stringify(x.times) === stringify(value)),
				tap((x) => log.debug("break rule check", x)),
				takeWhile((x) => x, true)
			)
			.subscribe((x) => !x && this.error$.next(null));
	}
	isFieldShown(name: string) {
		return !this.hiddenFields.value?.[name];
	}
	get hasActiveRecording(): boolean {
		return this.group.controls.times.controls.some((timeControl) => timeControl.controls.recording.value === true);
	}
	/** Deletes all current form rows (persisted ids are deleted on backend). */
	async deleteAllTimes(confirmDeleteTemplate?: TemplateRef<any>) {
		const timesArr = this.group.controls.times;
		if (timesArr.length === 0) return;

		this.isLoading.update(true);
		this.group.disable();

		if (confirmDeleteTemplate) {
			const allowDelete = await firstValueFrom(
				this.dialog
					.open(confirmDeleteTemplate, {
						data: { entity: this.data },
					})
					.afterClosed()
			);
			if (allowDelete !== true) {
				this.isLoading.update(false);
				this.group.enable();
				return;
			}
		}

		const rows = timesArr.getRawValue();
		const idsToDelete = rows.map((row) => row.id).filter((id): id is string => typeof id === "string" && !!id);

		try {
			if (idsToDelete.length > 0) {
				await this.comegoService
					.delete(...(idsToDelete.map((id) => ({ id })) as any))
					.then(() => this.ref.close(idsToDelete));
			}
		} catch (err) {
			this.isLoading.update(false);
			this.group.enable();
			pushError(err);
		}
	}

	async deleteTimes(ids: string[]) {
		if (!ids?.length) return;
		await this.comegoService.delete(...(ids.map((id) => ({ id })) as any));
	}

	private getRemovedOpenRecordingIds(times: Array<ComeGoTime & { id?: string | null }>): string[] {
		const formIds = new Set(times.map((t) => t.id).filter(Boolean));
		return (this.data.time.value ?? [])
			.filter((d) => d.id && !d.end && !this.checkedOutRecordingIds.includes(d.id))
			.filter((d) => !formIds.has(d.id))
			.map((d) => d.id!);
	}

	/** Active recordings excluded from bulk add/update still need name-only patches. */
	private getRecordingMetadataUpdates(
		times: Array<ComeGoTime & { id?: string | null }>,
		recordingsToRecreate: { oldId: string }[]
	): ComegoTime[] {
		const recreatedIds = new Set(recordingsToRecreate.map((r) => r.oldId));
		return times.reduce<ComegoTime[]>((acc, r) => {
			if (!r.recording || !r.id || recreatedIds.has(r.id)) return acc;
			const original = this.data.time.value?.find((o) => o.id === r.id);
			if (!original) return acc;
			if ((r.name ?? null) === (original.name ?? null)) return acc;
			acc.push({ ...original, name: r.name ?? null });
			return acc;
		}, []);
	}

	async submit() {
		const pendingToggleRecording = this.pendingToggleRecording;
		this.pendingToggleRecording = null;
		let { times, user, date: now } = this.group.getRawValue();
		const removedOpenRecordingIds = this.getRemovedOpenRecordingIds(times);
		if (!times.length && removedOpenRecordingIds.length === 0 && this.checkedOutRecordingIds.length === 0) return;
		const validTypes: ComegoTimeType[] = ["work", "pause", "absence"];
		times = times.map((x) => {
			if (!validTypes.includes(x.type)) {
				x.typeId = x.type;
				x.type = "absence";
			}
			return x;
		});
		this.isLoading.update(true);

		const checkoutUpdates = this.checkedOutRecordingIds
			.map((id) => {
				const original = this.data.time.value?.find((o) => o.id === id);
				const end = this.checkedOutRecordingEnds.get(id);
				if (!original || !end) return null;
				return produce(original, (draft) => {
					draft.end = end < new Date(draft.start) ? new Date(draft.start).toISOString() : end.toISOString();
				});
			})
			.filter((x): x is ComegoTime => !!x);

		let checkoutResult: ComegoTime[] = [];
		if (checkoutUpdates.length > 0) {
			try {
				checkoutResult = await this.comegoService
					.update(...checkoutUpdates)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService));
			} catch (err: any) {
				this.isLoading.update(false);
				if (isWorkingHoursError(err?.error)) {
					this.error$.next(parseErrorResponse(err));
					this.checkValueOnceChange(times);
				} else {
					pushError(err);
				}
				return;
			}
		}

		const timesWithoutCheckout = times.filter((t) => !t.sourceRecordingId);
		const createTimes = timesWithoutCheckout.reduce((acc, r) => {
			const resolved = this.resolveFormEntryTimes(r, now);
			const start = resolved?.start ?? coerceTimeFormat(r.start, now);
			const end = resolved?.end ?? coerceTimeFormat(r.end, now);
			acc.push({ ...r, start, end, user });
			return acc;
		}, []);
		const recordingsToRecreate = timesWithoutCheckout
			.filter((d) => d.recording && d.id)
			.reduce<{ oldId: string; entry: any }[]>((acc, r) => {
				const original = this.data.time.value?.find((o) => o.id === r.id);
				if (!original) return acc;
				const originalStartHHMM = format(new Date(original.start), "HH:mm");
				const startChanged = r.start !== originalStartHHMM;
				const typeChanged = r.type !== original.type;
				if (!startChanged && !typeChanged) return acc;
				acc.push({
					oldId: r.id,
					entry: {
						name: r.name ?? null,
						type: r.type,
						start: coerceTimeFormat(r.start, now),
						end: null,
						user,
					},
				});
				return acc;
			}, []);
		const recordingMetadataUpdates = this.getRecordingMetadataUpdates(timesWithoutCheckout, recordingsToRecreate);
		let error: any;
		const shouldRecreateDay =
			this.data.forceRecreateDay ||
			timesWithoutCheckout.find((d) => !d.id) ||
			recordingsToRecreate.length > 0 ||
			!!pendingToggleRecording;
		const existingTimes = timesWithoutCheckout.filter((d) => d.id);
		const timesToDelete: { id: string }[] = uniqBy(
			[
				...((this.data.time.value?.length && xorBy(existingTimes, this.data.time.value as any, (d) => d.id)) ||
					[]),
				...removedOpenRecordingIds.map((id) => ({ id })),
			],
			"id"
		);
		if (shouldRecreateDay) {
			const idsToDelete = [
				...(this.data.time.value?.map((d) => (!d.end ? null : d.id)).filter(Boolean) ?? []),
				...removedOpenRecordingIds,
				...recordingsToRecreate.map((r) => r.oldId),
			].filter((id) => !this.checkedOutRecordingIds.includes(id));
			if (idsToDelete.length) {
				await this.deleteTimes(idsToDelete).catch((err) => {
					error = err;
					this.isLoading.update(false);
					pushError(err);
				});
			}
		} else if (timesToDelete.length) {
			await this.comegoService.delete(...(timesToDelete.map(({ id }) => ({ id })) as any));
		}
		if (error) return;
		if (shouldRecreateDay) {
			const timesToAdd = [
				...createTimes.filter((d) => !d.recording).map((d) => omit(d, "id") as any),
				...recordingsToRecreate.map((r) => r.entry),
			];
			if (pendingToggleRecording) {
				const { type, start, user: toggleUser } = pendingToggleRecording;
				timesToAdd.push({
					name: null,
					type,
					start,
					end: null,
					user: toggleUser,
				});
			}
			let resultTimes: ComegoTime[] = [...checkoutResult];
			if (timesToAdd.length > 0) {
				const addedTimes = await this.comegoService
					.add(...timesToAdd)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
					.catch(async (err: HttpErrorResponse) => {
						this.isLoading.update(false);
						if (isWorkingHoursError(err.error)) {
							this.error$.next(parseErrorResponse(err));
							this.checkValueOnceChange(times);
						} else {
							pushError(err);
						}
						return Promise.reject(err);
					});
				resultTimes = [...resultTimes, ...(addedTimes ?? [])];
			}
			if (recordingMetadataUpdates.length > 0) {
				const updatedRecordings = await this.comegoService
					.update(...recordingMetadataUpdates)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
					.catch(async (err: HttpErrorResponse) => {
						this.isLoading.update(false);
						if (isWorkingHoursError(err.error)) {
							this.error$.next(parseErrorResponse(err));
							this.checkValueOnceChange(times);
						} else {
							pushError(err);
						}
						return Promise.reject(err);
					});
				resultTimes = [...resultTimes, ...(updatedRecordings ?? [])];
			}
			this.app.notifySuccess("success.updated-plural");
			this.ref.close(resultTimes);
		} else {
			const timesToUpdate = [...createTimes.filter((d) => !d.recording), ...recordingMetadataUpdates];
			if (timesToUpdate.length > 0) {
				await this.comegoService
					.update(...timesToUpdate)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
					.catch(async (err: HttpErrorResponse) => {
						this.isLoading.update(false);
						if (isWorkingHoursError(err.error)) {
							this.error$.next(parseErrorResponse(err));
							this.checkValueOnceChange(times);
						} else {
							pushError(err);
						}
						return Promise.reject(err);
					})
					.then((updatedTimes) => {
						this.app.notifySuccess("success.updated-plural");
						this.ref.close([...checkoutResult, ...(updatedTimes ?? [])]);
					});
			} else if (checkoutResult.length > 0) {
				this.app.notifySuccess("success.updated-plural");
				this.ref.close(checkoutResult);
			} else {
				this.app.notifySuccess("success.updated-plural");
				this.ref.close([]);
			}
		}
		this.isLoading.update(false);
	}

	allowedDates() {
		const pastDate = getComegoLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		const futureDate = getComegoFutureLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		return [pastDate, futureDate];
	}

	openCalPicker() {
		const hasActiveRecording = this.group.controls.times.controls.some(
			(timeControl) => timeControl.controls.recording.value === true
		);
		if (hasActiveRecording) {
			return;
		}

		const dates = this.allowedDates();
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(this.group.getRawValue().date),

					minDate: dates[0],
					maxDate: dates[1],
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.markFormAsChanged();
				this.patchValue({
					date: x,
				});
			});
	}
	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
			this.markFormAsChanged();
		}
	}
	calcType(type: string) {
		switch (type) {
			case "work_history":
			case "pause":
			case "home_filled":
				return type;
			default:
				return "beach_access";
		}
	}
	async changeDescriptionsForToday(currentDescription: string, applyToAllDialog?: TemplateRef<any>) {
		const timeControls: FormArray<TimesControl> = this.group.controls.times;
		//get value if this is the 2nd change
		const edited = this.userHasChangedDescription.value;
		if (!currentDescription || edited || timeControls.controls.length < 2) return;
		this.userHasChangedDescription.next(true);
		//confirm replaceAll dialog
		const replaceAll = await toPromise(
			this.dialog
				.open(applyToAllDialog, {
					data: {
						entity: null,
					},
				})
				.afterClosed()
		);
		if (replaceAll) {
			for (let formGroup of timeControls.controls) {
				if (formGroup.controls.type.value === "pause") continue;
				formGroup.controls.name.setValue(currentDescription);
			}
		}
	}

	parseTimeDurationFromType(formGroup: any) {
		const userSettings = this.userSettingsQuery.getValue();
		const absenceType = this.absenceSubcategories.find((absence) => absence.id === formGroup.controls.type.value);
		const userId = this.group.controls.user.value.id;
		formGroup.controls.end.enable();
		if (!absenceType) return;
		if (absenceType.isStatic || absenceType.durationType == "basedOnTargetHours") {
			if (!isAdmin(userSettings) && !isSubordinate(userSettings, userId)) formGroup.controls.end.disable();
		}
		const date = this.group.controls.date.value;
		let duration = "";
		if (absenceType.durationType == "basedOnTargetHours") {
			formGroup.controls.end.enable();
			duration = this.getScheduleForUser(userId, date);
		} else {
			duration = absenceType.duration;
		}
		const endString = this.sumTimes([formGroup.controls.start.value, duration]);
		formGroup.controls.end.patchValue(endString);
		this.sortTimes();
	}

	getScheduleForUser(userId: string, date: Date) {
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: userId }, date);

		if (!times || times.length == 0) return "00:00";

		let hours: string[] = [];
		times
			.filter((time) => {
				return time.type == "work";
			})
			.forEach((time) => {
				hours.push(this.calculateScheduleDuration(time));
			});

		return this.sumTimes(hours);
	}
	sumTimes(times: string[]): string {
		let totalMinutes = 0;

		times.forEach((time) => {
			if (!time) return;
			const parts = time.split(":");
			if (parts.length < 2) return;
			const [hours, minutes] = parts.map(Number);
			if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
			totalMinutes += hours * 60 + minutes;
		});

		const sumHours = Math.floor(totalMinutes / 60);
		const sumMinutes = totalMinutes % 60;

		return `${String(sumHours).padStart(2, "0")}:${String(sumMinutes).padStart(2, "0")}`;
	}
	calculateScheduleDuration(entry: ComeGoTime): string {
		if (!entry?.start || !entry?.end) return "00:00";
		const [startHour, startMinute] = entry.start.split(":").map(Number);
		const [endHour, endMinute] = entry.end.split(":").map(Number);
		if ([startHour, startMinute, endHour, endMinute].some((n) => Number.isNaN(n))) return "00:00";

		const startTotal = startHour * 60 + startMinute;
		const endTotal = endHour * 60 + endMinute;

		const diffMinutes = endTotal - startTotal;

		const hours = Math.floor(diffMinutes / 60);
		const minutes = diffMinutes % 60;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	}

	readonly absencesForUser = useAbsencesForUser();

	hasVisibleAbsenceSubcategories(subcategories: absenceSubcategories[] | null | undefined): boolean {
		return (subcategories ?? []).some((sub) => this.absencesForUser(sub, this.groupValue));
	}

	absenceSubmenuOpenIndex: number | null = null;
	private absenceSubmenuCloseTimer?: ReturnType<typeof setTimeout>;

	openAbsenceSubmenu(index: number): void {
		clearTimeout(this.absenceSubmenuCloseTimer);
		this.absenceSubmenuOpenIndex = index;
	}

	scheduleCloseAbsenceSubmenu(): void {
		clearTimeout(this.absenceSubmenuCloseTimer);
		this.absenceSubmenuCloseTimer = setTimeout(() => {
			this.absenceSubmenuOpenIndex = null;
		}, 100);
	}

	toggleAbsenceSubmenu(index: number): void {
		this.absenceSubmenuOpenIndex = this.absenceSubmenuOpenIndex === index ? null : index;
	}

	closeAbsenceSubmenu(): void {
		clearTimeout(this.absenceSubmenuCloseTimer);
		this.absenceSubmenuOpenIndex = null;
	}

	isAbsenceSubcategoryType(type: string | null | undefined): boolean {
		if (!type || type === "work" || type === "pause" || type === "absence") return false;
		return (this.absenceSubcategories ?? []).some((sub) => sub.id === type);
	}

	onTypeSelectOpenedChange(opened: boolean, index: number, currentType: string | null | undefined): void {
		if (opened && this.isAbsenceSubcategoryType(currentType)) {
			this.openAbsenceSubmenu(index);
		} else if (!opened) {
			this.closeAbsenceSubmenu();
		}
	}

	async loadExistingEntries() {
		const currentDate = this.group.getRawValue().date;
		if (!currentDate || !this.originalDate) return;

		// Check if the selected day is different from the original one
		if (!isSameDay(currentDate, this.originalDate)) {
			const user = this.group.getRawValue().user as WorkspaceUser;
			if (!user) return;

			// Ensure the selected day's week is loaded so we can show existing entries (e.g. when switching weeks)
			await this.ensureComegoLoadedForDate(currentDate, user.id);

			const raw = this.comegoQuery.getAll({
				filterBy: (entry) => !!isSameDay(new Date(entry.start), currentDate) && !!(entry.user?.id === user.id),
				sortBy: (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
			});
			// Query/store can return unstable order (same as create dialog); sort so index 0 = first of day
			const allEntries = [...raw].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
			this.existingEntries$.next(allEntries);
		} else {
			this.existingEntries$.next([]);
		}
	}

	formatTimeForDisplay(dateString: string | Date): string {
		const date = typeof dateString === "string" ? new Date(dateString) : dateString;
		return format(date, "HH:mm");
	}

	calculateExistingEntryDuration(entry: ComegoTime): number {
		if (!entry.start || !entry.end) return 0;
		const start = typeof entry.start === "string" ? new Date(entry.start) : entry.start;
		const end = typeof entry.end === "string" ? new Date(entry.end) : entry.end;
		return (end.getTime() - start.getTime()) / 1000; // return in seconds for parseMsAsDuration pipe
	}

	trackByEntryId(index: number, entry: ComegoTime): string {
		return entry.id || `${entry.start}-${entry.end}-${index}`;
	}

	async deleteExistingEntries() {
		const existingEntries = this.existingEntries$.value;
		if (!existingEntries || existingEntries.length === 0) return;

		// Show confirmation dialog
		const confirmed = await firstValueFrom(
			this.dialog
				.open(this.confirmDeleteDialog, {
					disableClose: true,
				})
				.afterClosed()
		);

		if (!confirmed) return;

		this.isDeletingEntries$.update(true);

		if (existingEntries.length > 0) {
			const viewId = `dialog-${Date.now()}`;
			this.lastDeletedViewId = viewId;
			const { date } = this.group.getRawValue();
			const dayStr = date && isValid(date) ? date : viewId;
			try {
				await this.deletionService.deleteWithUndo({
					type: "comegoDay",
					view: { id: viewId, times: existingEntries },
					day: dayStr,
				});
				this.existingEntries$.next([]);
			} catch (error) {
				this.lastDeletedViewId = null;
				// Restore entries if deletion fails (keep chronological order)
				this.existingEntries$.next(
					[...existingEntries].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
				);
				pushError(error);
			} finally {
				this.isDeletingEntries$.update(false);
			}
		} else {
			this.isDeletingEntries$.update(false);
		}
	}
}
